import { api, CACHE_TIME, getHeaders } from '@/api';
import { validateResponse } from '@/api/utils';
import type { CodeString } from '@/consts/codes';
import { i18n } from '@/i18n/i18n';
import { getUserId } from '@/stores/useAuthStore';
import { queryOptions } from '@tanstack/react-query';
import { toast } from 'sonner';

import { settingsArrayApiResponseSchema, type ApiResponse, type Setting } from './schema';
import { buildSettingsQuery, extractFirstSetting, parseSettingsData } from './utils';

export const SettingCategory = {
  Favorites: 'User.Web.FavoriteMenuItems',
  Recents: 'User.Web.RecentMenuItems',
  GridSaveLayout: 'User.Web.GridSaveLayout'
} as const;

export type SettingCategory = (typeof SettingCategory)[keyof typeof SettingCategory];

export const fetchSettings = async (category: SettingCategory): Promise<ApiResponse<Setting[] | null | undefined>> => {
  const userId = getUserId();
  const query = buildSettingsQuery(category, userId);
  const response = await api.get<ApiResponse<Setting[]>>(`md/api/Settings?${query}`, getHeaders());
  return validateResponse(settingsArrayApiResponseSchema, response.data);
};

export const settingsEndpoints = {
  getSettings: async (category: SettingCategory): Promise<CodeString[]> => {
    try {
      const parsed = await fetchSettings(category);
      const setting = extractFirstSetting(parsed);
      if (!setting?.Data) return [];
      return parseSettingsData(setting.Data);
    } catch (error) {
      console.error(`Error fetching ${category}:`, error);
      toast.error(i18n.t('error.settings.get'));
      return [];
    }
  }
};

export const settingsQueries = {
  all: (category: SettingCategory) => ['settings', category] as const,
  list: (category: SettingCategory) =>
    queryOptions({
      queryKey: settingsQueries.all(category),
      queryFn: () => settingsEndpoints.getSettings(category),
      staleTime: CACHE_TIME.STALE,
      gcTime: CACHE_TIME.GC
    })
};
